import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingBag, X } from 'lucide-react';
import { useSettings } from '@/hooks/useProducts';
import api from '@/lib/api';

interface Purchase {
  product_name: string;
  city: string;
  timestamp: string;
}

export default function SocialProofToasts() {
  const { t } = useTranslation('upsell');
  const { data } = useSettings();
  const [currentPurchase, setCurrentPurchase] = useState<Purchase | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const purchasesRef = useRef<Purchase[]>([]);
  const indexRef = useRef(0);

  const config = data?.settings?.upsell_config?.social_proof_toasts;

  useEffect(() => {
    if (!config?.enabled) return;

    // Fetch recent purchases
    api.get<{ purchases: Purchase[] }>('/social-proof/recent')
      .then((res) => {
        const purchases = (res.purchases || []).filter(
          (p) => p.product_name && p.city
        );
        purchasesRef.current = purchases;
      })
      .catch(() => { purchasesRef.current = []; });
  }, [config?.enabled]);

  useEffect(() => {
    if (!config?.enabled || !purchasesRef.current.length) return;

    const intervalMs = (config.interval_seconds || 30) * 1000;
    const displayMs = (config.display_duration_seconds || 5) * 1000;

    const timer = setInterval(() => {
      const purchases = purchasesRef.current;
      if (purchases.length === 0) return;

      const purchase = purchases[indexRef.current % purchases.length];
      indexRef.current++;

      setCurrentPurchase(purchase);
      setIsVisible(true);

      setTimeout(() => setIsVisible(false), displayMs);
    }, intervalMs);

    return () => clearInterval(timer);
  }, [config?.enabled, config?.interval_seconds, config?.display_duration_seconds]);

  if (!config?.enabled) return null;

  return (
    <div className="fixed bottom-4 left-4 z-40 max-w-xs" aria-live="polite">
      <AnimatePresence>
        {isVisible && currentPurchase && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.3 }}
            className="bg-surface rounded-lg shadow-lg border border-line p-3 flex items-start gap-3"
          >
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-sage-100">
              <ShoppingBag className="h-4 w-4 text-sage-600" aria-hidden="true" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-ink font-medium truncate">
                {currentPurchase.product_name}
              </p>
              <p className="text-xs text-ink-muted">
                {t('socialProof.someonePurchased', { city: currentPurchase.city })}
              </p>
            </div>
            <button
              onClick={() => setIsVisible(false)}
              className="text-ink-muted hover:text-ink-soft p-0.5"
              aria-label={t('socialProof.dismissAriaLabel')}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
